import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CssBaseline,
  MenuItem,
  FormControl,
  FormLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import Toast from "@/components/toast";
import Select from "react-select";
import { getSingleSelectStyles } from "@/lib/styles";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";

import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

// Custom styles for phone input to match MUI design
const phoneInputStyles = `
  .PhoneInput {
    position: relative;
  }
  .PhoneInput:focus-within {
    border-color: #1976d2;
  }
  .PhoneInputCountry {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 14px;
  }
  .PhoneInputInput {
    padding-left: 56px !important;
    background: none;
    border: none;
    font-family: inherit;
    font-size: 1rem;
    width: 100%;
    height: 100%;
    outline: none;
  }
  .PhoneInputInput::placeholder {
    color: rgba(0, 0, 0, 0.6);
  }
`;
import { getNames, getCode } from "country-list";
import { State, City } from "country-state-city";
import useGetSubscriptionPlans from "@/hooks/useGetSubscriptionPlans";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import useGetActiveCheckoutSession from "@/hooks/useGetActiveCheckoutSession";
import { format } from "date-fns";

export default function SubscriptionForm() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [portalEl, setPortalEl] = useState(null);
  const singleSelectStyles = getSingleSelectStyles(theme);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [activeSessionDialog, setActiveSessionDialog] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const handleToastClose = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  // Import and use subscription plans hook - enabled on this page
  const { plans } = useGetSubscriptionPlans(true);

  // Check for active checkout session
  const { checkoutSession, checkoutSessionLoading } =
    useGetActiveCheckoutSession(true);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("CREDIT_CARD");

  // Subscription type state
  const [subscriptionType, setSubscriptionType] = useState("INDIVIDUAL");

  // Subscription period state
  const [subscriptionPeriod, setSubscriptionPeriod] = useState("YEARLY");

  // Currency state
  const [selectedCurrency, setSelectedCurrency] = useState("USD");

  // Selected plan details state for INTERNAL plans (used for future enhancements)
  // eslint-disable-next-line no-unused-vars
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);

  // Available currencies (including USD for future expansion)
  const availableCurrencies = useMemo(() => {
    if (!plans) return ["USD", "EUR", "TRY"];

    const planCurrencies = [...new Set(plans.map((plan) => plan.currency))];
    // Add USD to available currencies and ensure it's included
    const allCurrencies = [...new Set([...planCurrencies, "USD"])];
    return allCurrencies.sort();
  }, [plans]);

  // Format subscription plans for the select input based on payment method, period, and currency
  const subscriptionPlans = useMemo(() => {
    if (!plans) return [];

    // Filter plans based on payment method, subscription period, and currency
    const filteredPlans = plans.filter((plan) => {
      // Filter by payment method
      let matchesPaymentMethod = false;
      if (paymentMethod === "CREDIT_CARD") {
        matchesPaymentMethod = plan.subscription_channel === "IYZICO";
      } else if (paymentMethod === "BANK_TRANSFER") {
        matchesPaymentMethod = plan.subscription_channel === "INTERNAL";
      }

      // Filter by subscription period
      const matchesPeriod = plan.billing_interval === subscriptionPeriod;

      // Filter by currency
      const matchesCurrency = plan.currency === selectedCurrency;

      return matchesPaymentMethod && matchesPeriod && matchesCurrency;
    });

    return filteredPlans.map((plan) => ({
      value: plan.id,
      label: plan.name, // Use plan name instead of internal_key
      description: plan.description,
      price: plan.price,
      currency: plan.currency,
      interval: plan.billing_interval,
      intervalCount: plan.billing_interval_count,
      maxUsers: plan.max_users,
      maxWorkPermits: plan.max_work_permits,
      maxObservations: plan.max_observations,
      trialDays: plan.trial_days,
      subscriptionChannel: plan.subscription_channel,
    }));
  }, [plans, paymentMethod, subscriptionPeriod, selectedCurrency]);

  // Set portal element and language
  useEffect(() => {
    setPortalEl(document.body);
    const savedLanguage = localStorage.getItem("ln");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Show active session dialog when session is found
  useEffect(() => {
    if (checkoutSession && !checkoutSessionLoading) {
      setActiveSessionDialog(true);
    }
  }, [checkoutSession, checkoutSessionLoading]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [formData, setFormData] = useState({
    address: "",
    city: "",
    state_province: "",
    state_code: "", // Added for internal use
    country: "",
    country_code: "",
    zip_code: "",
    identity_number: "",
    tax_office: "",
    email: "",
    gsm_number: "",
    name: "",
    surname: "",
    subscription_plan_id: "",
    preferred_invoice_language: localStorage.getItem("ln") || "en",
  });

  // Track which fields have been touched (blurred)
  const [touchedFields, setTouchedFields] = useState({});

  // Currency validation for Turkey + Credit Card
  const currencyValidationError = useMemo(() => {
    // Only show error if Turkey is selected, Credit Card payment method, and non-TRY currency
    if (
      formData.country_code === "TR" &&
      paymentMethod === "CREDIT_CARD" &&
      selectedCurrency !== "TRY"
    ) {
      return t(
        "subscription.currencyNotAllowed",
        "Foreign currency cannot be selected for Turkish residents using credit card payment."
      );
    }
    return null;
  }, [formData.country_code, paymentMethod, selectedCurrency, t]);

  // Get selected plan details
  const getSelectedPlan = useCallback(() => {
    if (!formData.subscription_plan_id || !plans) return null;
    return plans.find((plan) => plan.id === formData.subscription_plan_id);
  }, [formData.subscription_plan_id, plans]);

  // Update selected plan details when plan changes
  useEffect(() => {
    const selectedPlan = getSelectedPlan();
    if (selectedPlan && selectedPlan.subscription_channel === "INTERNAL") {
      setSelectedPlanDetails({
        name: selectedPlan.name,
        price: selectedPlan.price,
        currency: selectedPlan.currency,
        billing_interval: selectedPlan.billing_interval,
        billing_interval_count: selectedPlan.billing_interval_count,
      });
    } else {
      setSelectedPlanDetails(null);
    }
  }, [getSelectedPlan]);

  // Helper function to format price
  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  // Helper function to format billing interval
  const formatBillingInterval = (interval) => {
    return interval === "MONTHLY"
      ? t("subscription.monthly")
      : t("subscription.yearly");
  };

  // Initialize countries list
  useEffect(() => {
    const countryList = getNames()
      .map((name) => ({
        code: getCode(name),
        name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setCountries(countryList);
  }, []);

  // Update states when country changes
  useEffect(() => {
    if (formData.country_code) {
      const stateList = State.getStatesOfCountry(formData.country_code)
        .map((state) => ({
          code: state.isoCode,
          name: state.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setStates(stateList);
      setCities([]); // Reset cities when country changes
    } else {
      setStates([]);
      setCities([]);
    }
  }, [formData.country_code]);

  // Update cities when state changes
  useEffect(() => {
    if (formData.country_code && formData.state_code) {
      const cityList = City.getCitiesOfState(
        formData.country_code,
        formData.state_code
      )
        .map((city) => ({
          code: city.name,
          name: city.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCities(cityList);
    } else {
      setCities([]);
    }
  }, [formData.country_code, formData.state_code]);

  // Phone number is now handled directly in formData

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleBlur = (fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  // Validation helper functions
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateTCKN = (tckn) => {
    return /^\d{11}$/.test(tckn);
  };

  // Helper functions to check if field should show error
  const shouldShowError = (fieldName, validationFn) => {
    return touchedFields[fieldName] && !validationFn();
  };

  // Field validation functions
  const isNameValid = () => formData.name && formData.name.trim();
  const isSurnameValid = () => formData.surname && formData.surname.trim();
  const isEmailValid = () => formData.email && validateEmail(formData.email);
  const isPhoneValid = () => formData.gsm_number;
  const isCountryValid = () => formData.country_code && formData.country;
  const isStateValid = () => formData.state_province;
  const isAddressValid = () => formData.address && formData.address.trim();
  const isZipCodeValid = () => formData.zip_code && formData.zip_code.trim();
  const isPlanValid = () => formData.subscription_plan_id;

  const isIdentityValid = () => {
    if (!formData.identity_number || !formData.identity_number.trim())
      return false;
    if (subscriptionType === "INDIVIDUAL" && formData.country_code === "TR") {
      return validateTCKN(formData.identity_number);
    }
    return true; // For corporate or non-TR countries, any non-empty value is valid
  };

  const isTaxOfficeValid = () => {
    // Tax office is required only for corporate subscriptions in Turkey
    if (subscriptionType === "CORPORATE" && formData.country_code === "TR") {
      return formData.tax_office && formData.tax_office.trim();
    }
    return true; // Not required for other cases
  };

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    // Payment method is required
    if (!paymentMethod) return false;

    // Currency validation error exists
    if (currencyValidationError) return false;

    // Required fields for all cases
    if (!formData.subscription_plan_id) return false;
    if (!formData.name || !formData.name.trim()) return false;
    if (!formData.surname || !formData.surname.trim()) return false;
    if (!formData.email || !validateEmail(formData.email)) return false;
    if (!formData.gsm_number) return false;
    if (!formData.country_code || !formData.country) return false;
    if (!formData.state_province) return false;
    if (!formData.address || !formData.address.trim()) return false;
    if (!formData.zip_code || !formData.zip_code.trim()) return false;

    // Identity/Tax/Passport number validation
    if (subscriptionType === "INDIVIDUAL" && formData.country_code === "TR") {
      // Turkish individuals must provide valid TCKN
      if (!formData.identity_number || !validateTCKN(formData.identity_number))
        return false;
    } else {
      // Corporate or other countries must provide tax number or passport number
      if (!formData.identity_number || !formData.identity_number.trim())
        return false;
    }

    // Tax office validation - only required for corporate Turkey subscriptions
    if (subscriptionType === "CORPORATE" && formData.country_code === "TR") {
      if (!formData.tax_office || !formData.tax_office.trim()) return false;
    }

    return true;
  }, [formData, paymentMethod, subscriptionType, currencyValidationError]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const { mutate: postSubscription, isLoading: isPosting } = usePostJsonData();

  // Shared function to inject payment scripts
  const injectPaymentScripts = (checkoutFormContent) => {
    const container = document.getElementById("iyzipay-checkout-form");
    if (!container) return;

    // Clear existing content
    container.innerHTML = "";

    // Create a div for the form
    const formDiv = document.createElement("div");
    formDiv.className = "responsive";
    container.appendChild(formDiv);

    // First, remove any existing iyzipay scripts
    const existingScripts = document.querySelectorAll('script[src*="iyzipay"]');
    existingScripts.forEach((script) => script.remove());

    // Remove any existing iyzi* global variables
    window.iyziInit = undefined;
    window.iyziUcsInit = undefined;
    window.iyziSubscriptionInit = undefined;

    // Create a temporary div to parse scripts
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = checkoutFormContent;

    // Extract and execute scripts in order
    const scripts = tempDiv.getElementsByTagName("script");
    Array.from(scripts).forEach((script) => {
      const scriptContent = script.innerHTML;

      // Create a new script element
      const scriptEl = document.createElement("script");
      scriptEl.type = "text/javascript";

      // Add callback to iyziInit if present
      if (scriptContent.includes("iyziInit = {")) {
        const modifiedContent = scriptContent.replace(
          "var iyziInit = {",
          `var iyziInit = {
            callback: function(result) {
             
              if (result.status === 'success') {
                fetch('/api/v1/subscriptions/active', {
                  headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                    'Accept-Language': localStorage.getItem('ln') || 'en'
                  }
                })
                .then(response => response.json())
                .then(data => {
                
                  window.location.href = '/payment-success' + window.location.search;
                })
                .catch(error => console.error("Error fetching subscription:", error));
              }
            },`
        );
        scriptEl.innerHTML = modifiedContent;
      } else {
        scriptEl.innerHTML = scriptContent;
      }

      // Execute the script
      document.head.appendChild(scriptEl);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    try {
      // Remove internal fields from payload and add required fields
      const { state_code: _, ...dataWithoutStateCode } = formData; // eslint-disable-line no-unused-vars
      const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      const payload = {
        ...dataWithoutStateCode,
        preferred_invoice_language:
          formData.preferred_invoice_language ||
          localStorage.getItem("ln") ||
          "en",
        success_url: `${baseUrl}/payment-success`,
        cancel_url: `${baseUrl}/payment-cancelled`,
        payment_method: paymentMethod, // Add payment method to payload
        // For Individual + Turkey, send "Not Required" as tax_office value
        ...(subscriptionType === "INDIVIDUAL" &&
          formData.country_code === "TR" && {
            tax_office: "Not Required",
          }),
      };

      postSubscription(
        { url: "/api/v1/subscriptions", data: payload },
        {
          onSuccess: (response) => {
            // For credit card payments (IYZICO), open payment modal
            if (
              paymentMethod === "CREDIT_CARD" &&
              response.checkout_form_content
            ) {
              setPaymentModalOpen(true);

              // Wait for DOM to be ready
              setTimeout(() => {
                injectPaymentScripts(response.checkout_form_content);
              }, 100);
            } else {
              // For bank transfer (INTERNAL) or non-payment subscriptions
              setToast({
                open: true,
                severity: "success",
                message:
                  paymentMethod === "BANK_TRANSFER"
                    ? t(
                        "subscription.bankTransferSuccess",
                        "Subscription request submitted. Please check your email for bank transfer details."
                      )
                    : t("subscription.successMessage"),
              });
              navigate("/");
            }
            setIsSubmitting(false);
          },
          onError: (error) => {
            console.error("Subscription error:", error);
            setSubmitError(
              error.response?.data?.message || t("subscription.errorMessage")
            );
            setIsSubmitting(false);
          },
        }
      );
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError(t("subscription.errorMessage"));
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{phoneInputStyles}</style>
      <CssBaseline enableColorScheme />
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          py: 4,
          background: (theme) => ({
            backgroundImage:
              "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
            ...theme.applyStyles("dark", {
              backgroundImage:
                "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
            }),
          }),
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                p: 4,
                background: (theme) =>
                  theme.palette.mode === "light"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`
                    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                borderBottom: "1px solid",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: (theme) =>
                    theme.palette.mode === "light"
                      ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                      : `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 2,
                }}
              >
                {t("subscription.title")}
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{
                  maxWidth: "600px",
                  mx: "auto",
                  lineHeight: 1.6,
                }}
              >
                {t("subscription.subtitle")}
              </Typography>
            </Box>

            {/* Form Section */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                p: 4,
                background: (theme) =>
                  theme.palette.mode === "light"
                    ? theme.palette.background.paper
                    : alpha(theme.palette.background.paper, 0.8),
              }}
            >
              {/* Configuration Section */}
              <Box
                sx={{
                  mb: 4,
                  p: 4,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? alpha(theme.palette.primary.main, 0.02)
                      : alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🎯{" "}
                  {t(
                    "subscription.configureSubscription",
                    "Configure Your Subscription"
                  )}
                </Typography>

                <Grid container spacing={4}>
                  {/* Row 1: Subscription Type */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        👤{" "}
                        {t("subscription.subscriptionFor", "Subscription For")}{" "}
                        *
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          "& .MuiButtonBase-root": {
                            flex: 1,
                            p: 2,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                            "&.Mui-selected": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
                            },
                          },
                        }}
                      >
                        <Tooltip
                          title={t(
                            "subscription.individualDesc",
                            "Personal subscription"
                          )}
                          arrow
                        >
                          <Button
                            variant="outlined"
                            className={
                              subscriptionType === "INDIVIDUAL"
                                ? "Mui-selected"
                                : ""
                            }
                            onClick={() => {
                              setSubscriptionType("INDIVIDUAL");
                              setFormData((prev) => ({
                                ...prev,
                                tax_office: "",
                              }));
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              👤 {t("subscription.individual", "Individual")}
                            </Typography>
                          </Button>
                        </Tooltip>
                        <Tooltip
                          title={t(
                            "subscription.corporateDesc",
                            "Business subscription"
                          )}
                          arrow
                        >
                          <Button
                            variant="outlined"
                            className={
                              subscriptionType === "CORPORATE"
                                ? "Mui-selected"
                                : ""
                            }
                            onClick={() => {
                              setSubscriptionType("CORPORATE");
                              setFormData((prev) => ({
                                ...prev,
                                tax_office: "",
                              }));
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              🏢 {t("subscription.corporate", "Corporate")}
                            </Typography>
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Row 1: Payment Method */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        💳 {t("subscription.paymentMethod", "Payment Method")} *
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          "& .MuiButtonBase-root": {
                            flex: 1,
                            p: 2,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                            "&.Mui-selected": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
                            },
                          },
                        }}
                      >
                        <Tooltip
                          title={t(
                            "subscription.creditCardDesc",
                            "Pay instantly with your credit card"
                          )}
                          arrow
                        >
                          <Button
                            variant="outlined"
                            className={
                              paymentMethod === "CREDIT_CARD"
                                ? "Mui-selected"
                                : ""
                            }
                            onClick={() => {
                              setPaymentMethod("CREDIT_CARD");
                              setFormData((prev) => ({
                                ...prev,
                                subscription_plan_id: "",
                              }));
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              💳 {t("subscription.creditCard", "Credit Card")}
                            </Typography>
                          </Button>
                        </Tooltip>
                        <Tooltip
                          title={t(
                            "subscription.bankTransferDesc",
                            "Pay via bank transfer"
                          )}
                          arrow
                        >
                          <Button
                            variant="outlined"
                            className={
                              paymentMethod === "BANK_TRANSFER"
                                ? "Mui-selected"
                                : ""
                            }
                            onClick={() => {
                              setPaymentMethod("BANK_TRANSFER");
                              setFormData((prev) => ({
                                ...prev,
                                subscription_plan_id: "",
                              }));
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              🏦{" "}
                              {t("subscription.bankTransfer", "Bank Transfer")}
                            </Typography>
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Row 2: Billing Period */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        📅{" "}
                        {t(
                          "subscription.subscriptionPeriodFilter",
                          "Subscription Period"
                        )}{" "}
                        *
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          "& .MuiButtonBase-root": {
                            flex: 1,
                            p: 2,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                            "&.Mui-selected": {
                              borderColor: "primary.main",
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
                            },
                          },
                        }}
                      >
                        <Tooltip
                          title={t(
                            "subscription.monthlyPeriodDesc",
                            "Billed every month"
                          )}
                          arrow
                        >
                          <Button
                            variant="outlined"
                            className={
                              subscriptionPeriod === "MONTHLY"
                                ? "Mui-selected"
                                : ""
                            }
                            onClick={() => {
                              setSubscriptionPeriod("MONTHLY");
                              setFormData((prev) => ({
                                ...prev,
                                subscription_plan_id: "",
                              }));
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              📅 {t("subscription.monthlyPeriod", "Monthly")}
                            </Typography>
                          </Button>
                        </Tooltip>
                        <Tooltip
                          title={t(
                            "subscription.yearlyPeriodDesc",
                            "Billed every year"
                          )}
                          arrow
                        >
                          <Button
                            variant="outlined"
                            className={
                              subscriptionPeriod === "YEARLY"
                                ? "Mui-selected"
                                : ""
                            }
                            onClick={() => {
                              setSubscriptionPeriod("YEARLY");
                              setFormData((prev) => ({
                                ...prev,
                                subscription_plan_id: "",
                              }));
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              📆 {t("subscription.yearlyPeriod", "Yearly")}
                            </Typography>
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Row 2: Currency */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: currencyValidationError
                          ? "error.main"
                          : "divider",
                        bgcolor: "background.paper",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: currencyValidationError
                            ? "error.main"
                            : "primary.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        💰 {t("subscription.currency", "Currency")} *
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          flexWrap: "wrap",
                          "& .MuiButtonBase-root": {
                            minWidth: "80px",
                            p: 1.5,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor: currencyValidationError
                              ? "error.main"
                              : "divider",
                            bgcolor: "background.paper",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: currencyValidationError
                                ? "error.main"
                                : "primary.main",
                              bgcolor: currencyValidationError
                                ? alpha(theme.palette.error.main, 0.04)
                                : alpha(theme.palette.primary.main, 0.04),
                            },
                            "&.Mui-selected": {
                              borderColor: currencyValidationError
                                ? "error.main"
                                : "primary.main",
                              bgcolor: currencyValidationError
                                ? alpha(theme.palette.error.main, 0.08)
                                : alpha(theme.palette.primary.main, 0.08),
                              boxShadow: currencyValidationError
                                ? `0 0 0 1px ${alpha(theme.palette.error.main, 0.3)}`
                                : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
                            },
                          },
                        }}
                      >
                        {availableCurrencies.map((currency) => (
                          <Button
                            key={currency}
                            variant="outlined"
                            className={
                              selectedCurrency === currency
                                ? "Mui-selected"
                                : ""
                            }
                            onClick={() => {
                              setSelectedCurrency(currency);
                              setFormData((prev) => ({
                                ...prev,
                                subscription_plan_id: "",
                              }));
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              {currency === "USD" && "💵"}{" "}
                              {currency === "EUR" && "💶"}{" "}
                              {currency === "TRY" && "💰"} {currency}
                            </Typography>
                          </Button>
                        ))}
                      </Box>
                      {currencyValidationError && (
                        <Alert
                          severity="error"
                          sx={{
                            mt: 2,
                            borderRadius: 2,
                            "& .MuiAlert-message": { fontSize: "0.875rem" },
                          }}
                        >
                          {currencyValidationError}
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Selected Plan Details Display */}
              {formData.subscription_plan_id && getSelectedPlan() && (
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📋 {t("subscription.selectedPlan", "Selected Plan")}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("subscription.plan", "Plan")}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {getSelectedPlan().name}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("subscription.planPrice", "Price")}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color="primary.main"
                      >
                        {formatPrice(
                          getSelectedPlan().price,
                          getSelectedPlan().currency
                        )}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("subscription.planCurrency", "Currency")}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {getSelectedPlan().currency}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t(
                          "subscription.planBillingInterval",
                          "Billing Interval"
                        )}
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatBillingInterval(
                          getSelectedPlan().billing_interval
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Grid
                container
                spacing={3}
                sx={{
                  "& .MuiFormControl-root": {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      transition: (theme) =>
                        theme.transitions.create([
                          "border-color",
                          "box-shadow",
                        ]),
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        boxShadow: (theme) =>
                          `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    },
                    "& .MuiFormLabel-root": {
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      mx: 0,
                      mt: 1,
                    },
                  },
                }}
              >
                {/* Subscription Plan Selection */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <FormLabel
                      sx={{
                        mb: 1,
                        color: shouldShowError("plan", isPlanValid)
                          ? "error.main"
                          : "text.primary",
                      }}
                    >
                      {t("subscription.plan")} *
                    </FormLabel>
                    <Select
                      value={subscriptionPlans.find(
                        (plan) => plan.value === formData.subscription_plan_id
                      )}
                      onChange={(opt) =>
                        setFormData((prev) => ({
                          ...prev,
                          subscription_plan_id: opt.value,
                        }))
                      }
                      onBlur={() => handleBlur("plan")}
                      options={subscriptionPlans}
                      styles={{
                        ...singleSelectStyles,
                        control: (base, state) => ({
                          ...base,
                          minHeight: "56px", // Match MUI TextField height
                          fontSize: "1rem",
                          borderColor: shouldShowError("plan", isPlanValid)
                            ? theme.palette.error.main
                            : state.isFocused
                              ? theme.palette.primary.main
                              : theme.palette.divider,
                          boxShadow: state.isFocused
                            ? `0 0 0 2px ${theme.palette.primary.main}20`
                            : "none",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "8px 14px", // Match MUI TextField padding
                        }),
                        input: (base) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                      }}
                      menuPortalTarget={portalEl}
                      menuPosition="fixed"
                      isSearchable={false}
                    />
                    {shouldShowError("plan", isPlanValid) && (
                      <div
                        style={{
                          color: theme.palette.error.main,
                          fontSize: "0.75rem",
                          marginTop: "3px",
                          marginLeft: "14px",
                          marginRight: "14px",
                        }}
                      >
                        {t(
                          "subscription.planRequired",
                          "Subscription plan is required"
                        )}
                      </div>
                    )}
                  </FormControl>
                </Grid>

                {/* Invoice Language Selection */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 1 }}>
                      {t("subscription.preferredInvoiceLanguage")}
                    </FormLabel>
                    <TextField
                      select
                      fullWidth
                      name="preferred_invoice_language"
                      value={formData.preferred_invoice_language}
                      onChange={handleChange}
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="tr">Türkçe</MenuItem>
                      <MenuItem value="de">Deutsch</MenuItem>
                      <MenuItem value="es">Español</MenuItem>
                      <MenuItem value="fr">Français</MenuItem>
                      <MenuItem value="it">Italiano</MenuItem>
                      <MenuItem value="nl">Nederlands</MenuItem>
                      <MenuItem value="pt">Português</MenuItem>
                    </TextField>
                  </FormControl>
                </Grid>

                {/* Personal Information */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    name="name"
                    label={t("subscription.name")}
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={() => handleBlur("name")}
                    error={shouldShowError("name", isNameValid)}
                    helperText={
                      shouldShowError("name", isNameValid) &&
                      t("subscription.nameRequired", "Name is required")
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    name="surname"
                    label={t("subscription.surname")}
                    value={formData.surname}
                    onChange={handleChange}
                    onBlur={() => handleBlur("surname")}
                    error={shouldShowError("surname", isSurnameValid)}
                    helperText={
                      shouldShowError("surname", isSurnameValid) &&
                      t("subscription.surnameRequired", "Surname is required")
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    name="email"
                    type="email"
                    label={t("subscription.email")}
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => handleBlur("email")}
                    error={shouldShowError("email", isEmailValid)}
                    helperText={
                      shouldShowError("email", isEmailValid) &&
                      (formData.email && !validateEmail(formData.email)
                        ? t(
                            "subscription.invalidEmail",
                            "Please enter a valid email address"
                          )
                        : t("subscription.emailRequired", "Email is required"))
                    }
                  />
                </Grid>

                {/* Country Selection */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    required
                    fullWidth
                    label={t("subscription.country")}
                    value={formData.country_code}
                    onChange={(e) => {
                      const selectedCountry = countries.find(
                        (c) => c.code === e.target.value
                      );
                      setFormData((prev) => ({
                        ...prev,
                        country_code: e.target.value,
                        country: selectedCountry.name,
                        gsm_number: "", // Reset phone to ensure it matches new country format
                        state_province: "",
                        city: "",
                      }));
                    }}
                    onBlur={() => handleBlur("country")}
                    error={shouldShowError("country", isCountryValid)}
                    helperText={
                      shouldShowError("country", isCountryValid) &&
                      t("subscription.countryRequired", "Country is required")
                    }
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        top: "-8px",
                        left: "12px",
                        padding: "0 4px",
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 1,
                      }}
                    >
                      <label
                        style={{
                          fontSize: "0.75rem",
                          color: shouldShowError("phone", isPhoneValid)
                            ? theme.palette.error.main
                            : "rgba(0, 0, 0, 0.6)",
                          pointerEvents: "none",
                          display: "block",
                          transformOrigin: "left top",
                        }}
                      >
                        {t("subscription.phone")} *
                      </label>
                    </div>
                    <PhoneInput
                      international
                      defaultCountry={formData.country_code || "TR"}
                      value={formData.gsm_number}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          gsm_number: value || "",
                        }))
                      }
                      onBlur={() => handleBlur("phone")}
                      style={{
                        width: "100%",
                        height: "56px",
                        padding: "16.5px 14px",
                        border: `1px solid ${
                          shouldShowError("phone", isPhoneValid)
                            ? theme.palette.error.main
                            : "rgba(0, 0, 0, 0.23)"
                        }`,
                        borderRadius: "4px",
                        fontSize: "1rem",
                        backgroundColor: "transparent",
                      }}
                    />
                    {shouldShowError("phone", isPhoneValid) && (
                      <div
                        style={{
                          color: theme.palette.error.main,
                          fontSize: "0.75rem",
                          marginTop: "3px",
                          marginLeft: "14px",
                          marginRight: "14px",
                        }}
                      >
                        {t(
                          "subscription.phoneRequired",
                          "Phone number is required"
                        )}
                      </div>
                    )}
                  </div>
                </Grid>

                {/* Identity/Tax Number */}
                <Grid
                  size={{
                    xs: 12,
                    sm:
                      subscriptionType === "CORPORATE" &&
                      formData.country_code === "TR"
                        ? 6
                        : 12,
                  }}
                >
                  <TextField
                    fullWidth
                    required
                    name="identity_number"
                    label={
                      subscriptionType === "INDIVIDUAL" &&
                      formData.country_code === "TR"
                        ? t("subscription.identityNumber")
                        : t("subscription.taxNumber", "Tax Number")
                    }
                    value={formData.identity_number}
                    onChange={handleChange}
                    onBlur={() => handleBlur("identity_number")}
                    error={shouldShowError("identity_number", isIdentityValid)}
                    helperText={
                      shouldShowError("identity_number", isIdentityValid) &&
                      (subscriptionType === "INDIVIDUAL" &&
                      formData.country_code === "TR"
                        ? formData.identity_number &&
                          !validateTCKN(formData.identity_number)
                          ? t(
                              "subscription.tcknError",
                              "Please enter a valid 11-digit Turkish ID number"
                            )
                          : t(
                              "subscription.identityRequired",
                              "Identity number is required"
                            )
                        : t(
                            "subscription.taxNumberRequired",
                            "Tax number is required"
                          ))
                    }
                  />
                </Grid>

                {/* Tax Office - Only for Corporate + Turkey */}
                {subscriptionType === "CORPORATE" &&
                  formData.country_code === "TR" && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        required
                        name="tax_office"
                        label={t("subscription.taxOffice", "Tax Office")}
                        placeholder={t(
                          "subscription.taxOfficePlaceholder",
                          "Vergi Dairesi"
                        )}
                        value={formData.tax_office}
                        onChange={handleChange}
                        onBlur={() => handleBlur("tax_office")}
                        error={shouldShowError("tax_office", isTaxOfficeValid)}
                        helperText={
                          shouldShowError("tax_office", isTaxOfficeValid) &&
                          t(
                            "subscription.taxOfficeRequired",
                            "Tax office is required for corporate subscriptions in Turkey"
                          )
                        }
                      />
                    </Grid>
                  )}

                {/* Address Information */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    required
                    fullWidth
                    label={t("subscription.state")}
                    value={formData.state_code}
                    onChange={(e) => {
                      const selectedState = states.find(
                        (state) => state.code === e.target.value
                      );
                      setFormData((prev) => ({
                        ...prev,
                        state_province: selectedState.name,
                        state_code: e.target.value, // Keep code for city lookup
                        city: "",
                      }));
                    }}
                    onBlur={() => handleBlur("state")}
                    error={shouldShowError("state", isStateValid)}
                    helperText={
                      shouldShowError("state", isStateValid) &&
                      t(
                        "subscription.stateRequired",
                        "State/Province is required"
                      )
                    }
                  >
                    {states.map((state) => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label={t("subscription.city")}
                    value={formData.city}
                    helperText={
                      formData.state_code && cities.length === 0
                        ? t("subscription.cityNotAvailable")
                        : undefined
                    }
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }));
                    }}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.code} value={city.code}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={3}
                    name="address"
                    label={t("subscription.address")}
                    value={formData.address}
                    onChange={handleChange}
                    onBlur={() => handleBlur("address")}
                    error={shouldShowError("address", isAddressValid)}
                    helperText={
                      shouldShowError("address", isAddressValid)
                        ? t(
                            "subscription.addressRequired",
                            "Address is required"
                          )
                        : t("")
                    }
                    placeholder={t("subscription.addressPlaceholder")}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    name="zip_code"
                    label={t("subscription.zipCode")}
                    value={formData.zip_code}
                    onChange={handleChange}
                    onBlur={() => handleBlur("zip_code")}
                    error={shouldShowError("zip_code", isZipCodeValid)}
                    helperText={
                      shouldShowError("zip_code", isZipCodeValid) &&
                      t("subscription.zipCodeRequired", "Zip code is required")
                    }
                  />
                </Grid>

                {/* Submit Section */}
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      mt: 4,
                      pt: 3,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {submitError && (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "error.light",
                          "& .MuiAlert-message": {
                            width: "100%",
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {t("subscription.errorTitle", "Submission Error")}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {submitError}
                        </Typography>
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={!isFormValid || isSubmitting || isPosting}
                      sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 1.5,
                        color: "common.white",
                        boxShadow: (theme) =>
                          `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
                        background: (theme) =>
                          theme.palette.mode === "light"
                            ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                            : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        "&:hover": {
                          background: (theme) =>
                            theme.palette.mode === "light"
                              ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                              : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        },
                        "&:disabled": {
                          color: "common.white",
                          opacity: 0.6,
                        },
                      }}
                      startIcon={
                        (isSubmitting || isPosting) && (
                          <CircularProgress
                            size={20}
                            thickness={4}
                            sx={{ color: "inherit" }}
                          />
                        )
                      }
                    >
                      {isSubmitting || isPosting
                        ? t("subscription.submitting", "Submitting...")
                        : t("subscription.submit", "Complete Subscription")}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>
      {/* Payment Modal */}
      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            overflow: "visible", // Allow iframe to overflow
          },
        }}
      >
        <DialogTitle>
          {t("subscription.paymentTitle")}
          <IconButton
            aria-label="close"
            onClick={() => setPaymentModalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            id="iyzipay-checkout-form"
            className="responsive"
            sx={{
              minHeight: "500px", // Give enough space for the payment form
              position: "relative",
              "& iframe": {
                border: "none",
                width: "100%",
                height: "100%",
                minHeight: "500px",
              },
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Active Session Dialog */}
      <Dialog
        open={activeSessionDialog}
        onClose={() => setActiveSessionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("subscription.activeSession.title")}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t("subscription.activeSession.message")}
          </Typography>
          {checkoutSession?.expires_at && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("subscription.activeSession.expires", {
                time: format(new Date(checkoutSession.expires_at), "PPp"),
              })}
            </Typography>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setActiveSessionDialog(false)}
          >
            {t("subscription.activeSession.startNew")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setActiveSessionDialog(false);
              setPaymentModalOpen(true);

              // Wait for DOM to be ready
              setTimeout(() => {
                injectPaymentScripts(checkoutSession.checkout_form_content);
              }, 100); // Wait 100ms for DOM to be ready
            }}
          >
            {t("subscription.activeSession.continue")}
          </Button>
        </Box>
      </Dialog>

      {/* Toast for notifications */}
      <Toast
        open={toast.open}
        severity={toast.severity}
        message={toast.message}
        onClose={handleToastClose}
      />
    </>
  );
}
